<template>
  <div class="hp-form">
    <n-form
      :model="formData"
      :disabled="disabled"
      ref="formRef"
      :label-placement="labelPlacement"
      :label-width="labelWidth"
      :show-label="showLabel"
      :show-require-mark="showRequireMark"
      :rules="normalizedRules"
      :size="size"
      :show-feedback="showFeedback"
    >
      <n-grid
        :cols="cols"
        :collapsed="collapsed"
        :collapsed-rows="collapsedRows"
        :x-gap="xGap"
        :y-gap="yGap"
      >
        <n-gi
          v-for="formItem in visibleFormItemsList"
          :key="formItem.path"
          class="form-grid-item"
        >
          <n-form-item
            :label="formItem.label"
            :path="formItem.path"
            :rules="formItem.rules"
            :size="formItem.size"
          >
            <div class="form-item-content">
              <template v-if="readonly">
                <slot
                  v-if="formItem.slotName"
                  :name="formItem.slotName"
                  :formData="formData"
                  :formItem="formItem"
                  :value="formData[formItem.path]"
                  :readonly="true"
                >
                  <div>{{ getDisplayValue(formItem) }}</div>
                </slot>
                <div v-else>{{ getDisplayValue(formItem) }}</div>
              </template>
              <template v-else>
                <!-- slot 逻辑 -->
                <template v-if="['slot'].includes(formItem.type)">
                  <slot
                    :name="formItem.slotName"
                    :formData="formData"
                    :formItem="formItem"
                    :value="formData[formItem.path]"
                    :input="(val: any) => updateFormData(formItem.path, val)"
                    :change="(val: any) => updateFormData(formItem.path, val)"
                  />
                </template>
                <!-- 普通组件 -->
                <component
                  v-else
                  :is="getFieldComponent(formItem.type)"
                  v-bind="getFilteredProps(formItem)"
                  v-model="formData[formItem.path]"
                  :disabled="formItem.disabled ?? disabled"
                  :readonly="formItem.readonly ?? readonly"
                  @update:modelValue="(val: any) => handleModelValueUpdate(formItem, val)"
                ></component>
              </template>

              <!-- 提示文本 -->
              <div v-if="formItem.tip" class="form-item-tip">
                {{ formItem.tip }}
              </div>
            </div>
          </n-form-item>
        </n-gi>
      </n-grid>
    </n-form>
  </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, ref } from 'vue'
import customInput from './components/customInput.vue'
import { filterComponentProps, getComponent } from './utils/componentRegistry'

const formRef = ref()

export interface FormItem {
  path: string // 必传 key
  label?: string // 标题
  rules?: any[]
  type: string // 组件类型
  slotName?: string // slot名称
  tip?: string
  onChange?: (value: any) => void
  onInput?: (value: any) => void
  onBlur?: (value: any) => void
  onFocus?: (value: any) => void
  onRemove?: (value: any) => void
  beforeUpload?: (file: File) => boolean | Promise<void>
  [key: string]: any
}

interface Props {
  modelValue: Record<string, any>
  formItems: FormItem[]
  inline?: boolean
  labelWidth?: number | string | 'auto'
  labeAlign?: 'left' | 'right'
  labelPlacement?: 'left' | 'top'
  rules?: Record<string, any>
  showFeedback?: boolean
  disabled?: boolean
  readonly?: boolean
  showLabel?: boolean
  showRequireMark?: boolean
  requireMarkPlacement?: 'left' | 'right' | 'right-hanging'
  size?: 'small' | 'medium' | 'large'
  cols?: number
  collapsed?: boolean
  collapsedRows?: number
  xGap?: number
  yGap?: number
  tip?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  inline: true,
  labeAlign: 'right',
  labelPlacement: 'left',
  showFeedback: true,
  showLabel: true,
  showRequireMark: false,
  requireMarkPlacement: 'left',
  disabled: false,
  readonly: false,
  size: 'small',
  cols: 1,
  collapsed: false,
  collapsedRows: 1,
  xGap: 0,
  yGap: 0
})

const normalizedRules = computed(() => {
  const rules = props.rules || {}
  return Object.keys(rules).reduce<Record<string, any>>((result, path) => {
    const ruleList = Array.isArray(rules[path]) ? rules[path] : [rules[path]]
    result[path] = ruleList.map((rule) => ({
      ...rule,
      __hpFormPath: path
    }))
    return result
  }, {})
})

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, any>]
  change: [formItem: FormItem, value: any, formData: Record<string, any>]
  'field-change': [path: string, value: any, formItem: FormItem]
}>()

// 获取组件实例
const getFieldComponent = (type: string): Component => {
  const config = getComponent(type)
  if (config) return config.component
  // 如果未注册，返回输入框作为默认
  return customInput
}

// 合并组件属性
const getFilteredProps = (formItem: FormItem) => {
  const { type: itemType } = formItem
  const restProps: Record<string, any> = { ...formItem, type: itemType }
  delete restProps.path
  delete restProps.label
  delete restProps.rules
  const filtered = filterComponentProps(itemType, restProps)

  // 添加通用的props
  const universalProps = ['placeholder', 'clearable', 'bordered', 'rules']
  universalProps.forEach((prop) => {
    if (restProps[prop] !== undefined && !(prop in filtered)) {
      filtered[prop] = restProps[prop]
    }
  })

  // 处理事件回调（只保留非原生事件的回调）
  const eventProps = ['onBlur', 'onFocus', 'onRemove', 'beforeUpload']
  eventProps.forEach((eventProp) => {
    if (restProps[eventProp] !== undefined && !(eventProp in filtered)) {
      filtered[eventProp] = restProps[eventProp]
    }
  })
  return filtered
}

// 核心：formData 计算属性，绑定 v-model
const formData = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// 更新表单数据（核心优化：确保赋值的是「值」而非事件对象）
const updateFormData = (path: string, value: any, isFileId?: boolean) => {
  if (!path) return

  // 提取真实值：如果是事件对象，取 target.value；如果是文件，取 id；否则直接用值
  let realValue = value
  // 处理 InputEvent 事件对象
  if (value instanceof Event && (value.target as HTMLInputElement).value) {
    realValue = (value.target as HTMLInputElement).value
  }
  // 处理文件ID逻辑
  if (isFileId && realValue?.id) {
    realValue = realValue.id
  }

  // 深拷贝保证响应式
  formData.value = {
    ...JSON.parse(JSON.stringify(formData.value)),
    [path]: realValue
  }
}

// 处理modelValue更新（唯一的更新入口）
const handleModelValueUpdate = (formItem: FormItem, value: any) => {
  // console.log(`组件 ${formItem.path} 更新 modelValue:`, value)

  // 区分上传组件的特殊处理
  if (formItem.type === 'upload' && formItem?.isFileId) {
    updateFormData(formItem.path, value, true)
  } else {
    updateFormData(formItem.path, value)
  }

  // 触发自定义事件
  if (formItem.onChange && typeof formItem.onChange === 'function') {
    formItem.onChange(value)
  }
  emit('change', formItem, value, formData.value)
  emit('field-change', formItem.path, value, formItem)
}

const getOptions = (options: any) => {
  if (!options) return []
  if (Array.isArray(options)) return options
  if (Array.isArray(options.value)) return options.value
  return []
}

// 获取显示值（用于只读模式）
const getDisplayValue = (formItem: FormItem) => {
  const value = formData.value[formItem.path]
  // 如果是文件，显示文件名
  if (value instanceof File) {
    return value.name
  }
  // 如果是文件数组
  if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
    return value.map((file) => file.name).join(', ')
  }

  const options = getOptions(formItem.options)
  if (options.length) {
    const valueField = formItem.valueField || 'value'
    const labelField = formItem.labelField || 'label'
    const matched = options.find((item: any) => item?.[valueField] === value)
    if (matched?.[labelField]) return matched[labelField]
  }

  // 默认显示
  return value || '-'
}

// 过滤可见的表单项
const visibleFormItemsList = computed(() => {
  return props.formItems.filter(
    (item) => item.visible !== false && item.type && getComponent(item.type)
  )
})

// 暴露方法
defineExpose({
  formRef,
  validate: async () => {
    if (!formRef.value) return Promise.resolve(true)
    return await formRef.value.validate()
  },
  validateFields: async (fields: string[]) => {
    if (!formRef.value) return Promise.resolve(true)
    const fieldSet = new Set(fields)
    return await formRef.value.validate(undefined, (rule: any) => {
      return fieldSet.has(rule?.__hpFormPath)
    })
  },
  restoreValidation: () => {
    formRef.value?.restoreValidation()
  },
  getFormData: () => {
    return JSON.parse(JSON.stringify(formData.value))
  },
  resetForm: () => {
    // 1. 清除表单的校验提示状态
    formRef.value?.restoreValidation()
    // 2. 重置表单数据为父组件传入的初始值
    formData.value = JSON.parse(JSON.stringify(props.modelValue))
  }
})
</script>

<style scoped lang="scss">
.form-grid-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  .form-item-content {
    display: flex;
    flex-direction: column;
    // margin: 0 0 12px;
    gap: 4px; /* 输入框和提示的间距 */
    width: 100%;
    .form-item-tip {
      font-size: 12px;
      color: #86909c;
      line-height: 2;
      white-space: pre-line; /* 保留换行符 */
      margin: 0;
    }
  }
}
</style>
